<template>
  <div class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
    <div
      class="bg-bg-primary rounded-xl shadow-theme-lg p-6 sm:p-8 max-w-md w-full mx-4 theme-transition"
    >
      <h3 class="text-xl sm:text-2xl font-bold text-text-primary mb-4 text-center">
        Recording Attendance Video
      </h3>

      <div class="camera-container mb-6">
        <video
          ref="videoRef"
          v-show="!recordingComplete"
          autoplay
          muted
          playsinline
          class="camera-feed"
        ></video>

        <!-- Verification Overlay -->
        <div v-if="loading && !isRecording && !recordingComplete && !error" class="recording-overlay">
          <div class="flex flex-col items-center">
            <svg class="animate-spin h-16 w-16 text-accent-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-lg font-semibold text-white">Verifying your identity...</p>
            <p class="text-sm text-gray-300 mt-2">Please look at the camera</p>
          </div>
        </div>

        <!-- Recording Overlay -->
        <div v-if="isRecording && !recordingComplete" class="recording-overlay">
          <div class="recording-indicator">
            <div class="pulse-dot"></div>
            <span class="recording-text">Recording...</span>
          </div>
          <div class="countdown-timer">{{ countdown }}</div>
          <p class="text-sm text-white mt-4">Please look directly at the camera</p>
        </div>

        <!-- Recording Complete -->
        <div
          v-if="recordingComplete"
          class="recording-complete flex flex-col items-center justify-center h-full"
        >
          <svg class="w-16 h-16 text-success mb-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-lg font-semibold text-text-primary">Video Captured!</p>
          <p class="text-sm text-text-secondary mt-2">Processing...</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="error-state flex flex-col items-center justify-center h-full p-4">
          <svg class="w-16 h-16 text-error mb-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-lg font-semibold text-error mb-2">Recording Failed</p>
          <p class="text-sm text-text-secondary text-center">{{ error }}</p>
          <button
            @click="retryRecording"
            class="mt-4 px-4 py-2 bg-error text-white rounded-lg hover:bg-error hover:opacity-90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-4 justify-center">
        <button
          v-if="!isRecording && !recordingComplete && !error"
          @click="startRecording"
          :disabled="loading"
          class="bg-success hover:bg-success hover:opacity-90 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Start Recording
        </button>

        <button
          v-if="error || (!isRecording && recordingComplete)"
          @click="$emit('cancel')"
          class="bg-bg-tertiary hover:bg-bg-secondary text-text-primary px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import VideoCaptureUtil from '@/utils/media/videoCapture.js'
import { useFaceRecognitionStore } from '@/stores/faceRecognition'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'

const emit = defineEmits(['video-captured', 'cancel'])
const faceStore = useFaceRecognitionStore()
const authStore = useAuthStore()
const toast = useToast()

const videoRef = ref<HTMLVideoElement>()
const isRecording = ref(false)
const recordingComplete = ref(false)
const loading = ref(false)
const error = ref('')
const countdown = ref(3)

let stream: MediaStream | null = null
let countdownInterval: ReturnType<typeof setInterval> | null = null

// Helper function to capture a frame from video as base64
const captureFrameFromVideo = (videoElement: HTMLVideoElement): string => {
  const canvas = document.createElement('canvas')
  canvas.width = videoElement.videoWidth
  canvas.height = videoElement.videoHeight
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height)
  }
  return canvas.toDataURL('image/jpeg', 0.95).split(',')[1] // Return base64 without prefix
}

const startRecording = async () => {
  loading.value = true
  error.value = ''
  recordingComplete.value = false

  try {
    // Request camera access
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        facingMode: 'user',
      },
      audio: false,
    })

    if (videoRef.value) {
      videoRef.value.srcObject = stream
      await videoRef.value.play()

      // Wait for video to stabilize
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Check if user is loaded
      if (!authStore.user || !authStore.user.uid) {
        throw new Error('User not authenticated. Please log in again.')
      }

      // Capture a frame for face verification
      toast.info('Verifying your identity...')
      const imageBase64 = captureFrameFromVideo(videoRef.value)

      // Verify face before allowing recording
      const verificationResult = await faceStore.verifyFace(authStore.user.uid, imageBase64)

      // API returns 'matched' and 'similarity_score'
      const isMatched = verificationResult.matched
      const confidence = verificationResult.similarity_score

      if (!isMatched || confidence < 0.7) {
        throw new Error(`Face verification failed. Please ensure you are looking directly at the camera. (Confidence: ${Math.round(confidence * 100)}%)`)
      }

      toast.success(`Face verified! (${Math.round(confidence * 100)}% match)`)

      // Now proceed with video recording
      isRecording.value = true

      // Start countdown (3 seconds)
      const recordingDuration = 3
      countdown.value = recordingDuration
      
      countdownInterval = setInterval(() => {
        if (countdown.value > 0) {
          countdown.value--
        }
        if (countdown.value <= 0) {
          clearInterval(countdownInterval!)
          countdownInterval = null
        }
      }, 1000)

      // Record video for the same duration
      const videoBlob = await VideoCaptureUtil.recordVideo(stream, recordingDuration)

      console.log('Video captured, blob size:', videoBlob.size, 'bytes, type:', videoBlob.type)

      // Validate video
      const validation = await VideoCaptureUtil.validateVideo(videoBlob)
      if (!validation.isValid) {
        console.error('Video validation failed:', validation)
        throw new Error((validation as any).error || 'Invalid video')
      }

      console.log('Video validation passed, metadata:', validation.metadata)

      recordingComplete.value = true
      isRecording.value = false

      // Ensure blob is valid before emitting
      if (!videoBlob || videoBlob.size === 0) {
        throw new Error('Video blob is empty or invalid')
      }

      // Emit the video blob with actual confidence score (0-100 scale)
      const confidenceScore = confidence <= 1 ? confidence * 100 : confidence
      emit('video-captured', videoBlob, confidenceScore)
    }
  } catch (err: any) {
    console.error('Recording error:', err)

    // Handle face verification errors specifically
    if (err.message && err.message.includes('Face verification failed')) {
      error.value = err.message
    } else if (err.name === 'NotAllowedError') {
      error.value = 'Camera access denied. Please allow camera access.'
    } else if (err.name === 'NotFoundError') {
      error.value = 'No camera found. Please connect a camera.'
    } else if (err.name === 'SecurityError' || err.name === 'NotSupportedError' || err.message.includes('mimeType')) {
      error.value = 'Video recording is not supported on this browser/device. Please try another browser (like Chrome or Safari) or update your OS.'
    } else {
      error.value = err.message || 'Failed to record video. Please try again.'
    }

    isRecording.value = false
    recordingComplete.value = false
  } finally {
    loading.value = false
  }
}


const retryRecording = () => {
  error.value = ''
  recordingComplete.value = false
  if (stream) {
    stream.getTracks().forEach((track) => track.stop())
    stream = null
  }
}

onMounted(() => {
  // Auto-start recording when component mounts
  startRecording()
})

onUnmounted(() => {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop())
  }
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})
</script>

<style scoped>
.camera-container {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  background-color: var(--bg-tertiary);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.camera-feed {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.recording-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 1rem;
}

.recording-indicator {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.pulse-dot {
  width: 12px;
  height: 12px;
  background-color: #ef4444;
  border-radius: 50%;
  margin-right: 0.5rem;
  animation: pulse 1s infinite;
}

.recording-text {
  font-size: 1rem;
  font-weight: 600;
}

.countdown-timer {
  font-size: 4rem;
  font-weight: bold;
  color: white;
}

.recording-complete,
.error-state {
  position: absolute;
  inset: 0;
  background-color: var(--bg-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
